/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../core/state/state.service';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-attach-propulsion-scenario-pop-up',
    templateUrl: './attach-propulsion-scenario-pop-up.component.html',
    styleUrls: ['./attach-propulsion-scenario-pop-up.component.scss'],
    standalone: false
})
export class AttachPropulsionScenarioPopUpComponent implements OnInit {

  showSpinner: boolean;
  checkboxValue = false;

  selectedVehicleScenario: VehicleScenario;
  propScenarios: PropulsionScenario[] = [];
  columnHeaders: ColDef[] = [
    {field: 'id', headerName: 'ID', filter: AutocompleteFilterComponent},
    {field: 'forecastOrganization', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioName', headerName: 'Propulsion Scenario Name', filter: AutocompleteFilterComponent}
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };


  selectedRow: PropulsionScenario;

  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: any,
              public dialogRef: MatDialogRef<AttachPropulsionScenarioPopUpComponent>,
              readonly propulsionScenarioService: PropulsionScenarioService,
              readonly vehicleScenarioService: VehicleScenarioService,
              public user: StateService,
              public snackBar: FabMessageService) {
    if (data) {
      this.selectedVehicleScenario = data.selectedVehicleScenario;
    }
  }

  ngOnInit(): void {
    this.selectedRow = null;
    this.showSpinner = false;
    this.getPropScenListForAttachPropScen();
  }

  /**
   * Get the list of Propulsion Scenarios that can be attached to a specified Vehicle Scenario
   */
  getPropScenListForAttachPropScen(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getPropScenListForAttachPropScen(this.selectedVehicleScenario.fcorg, this.user.getUserGmid(),
      this.selectedVehicleScenario.vehScenId, this.checkboxValue ? 1 : 0).subscribe(data => {
      this.propScenarios = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Event handler for when the hidden checkbox is interacted with
   */
  onCheckboxChange(): void {
    this.getPropScenListForAttachPropScen();
  }

  /**
   * Sets the scenario as selected
   * @param event the event when a scenario is selected
   */
  onSelect(event: any) {
    const row: PropulsionScenario = event.data;
    if (event.node.selected) {
      this.selectedRow = row;
    } else if (event.data.id === this.selectedRow.id) {
      this.selectedRow = null;
    }
  }


  /**
   * Event handler for closing the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Event handler for when the user confirms what propulsion scenario to attach
   */
  onConfirm(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.updateAttachedPropulsionScenario(this.selectedRow.id, this.selectedVehicleScenario.vehScenId)
    .subscribe({
      error: err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
        this.dialogRef.close();
      },
      complete: () => {
        this.showSpinner = false;
        this.dialogRef.close(this.selectedRow);
        this.snackBar.displaySuccess('Propulsion scenario ' + this.selectedRow.id + ' has been successfully attached to ' +
          'Vehicle Scenario ' + this.selectedVehicleScenario.vehScenId, DISMISS_MSG);
      }
    });
  }
}
